import {useEffect,useMemo,useState,type FormEvent,type ReactNode} from 'react';

/** FloatHelm screenshots supplied October 29, 2026:
 * 130540: Waivers overview; 130603/130612: New Guest Waiver section editor;
 * 130619/130636: configuration and requirement rules;
 * 130643/130700/130709: non-submittable customer preview.
 * No legal clauses are fabricated. This screen manages waiver configuration;
 * customer signing and completed signatures remain a separate workflow.
 */
type Requirement={serviceTypeCode:string;locationCode:string};
const fields=['Phone','Postal Code','Address','Birthday',
  'How You Heard About Us','Emergency Contact Info'] as const;
type AccountField=typeof fields[number];
type Policy='No'|'Ask'|'Require';
type WaiverDraft={
  title:string;publicName:string;enabled:boolean;initialText:string;sections:string[];
  sectionInstructions:string;
  afterText:string;readAndAgreeText:string;submitLabel:string;
  requiresLoginInShop:boolean;requiresLoginOutOfShop:boolean;
  requireFullNameInShop:boolean;requireFullNameOutOfShop:boolean;
  requireSignatureInShop:boolean;requireSignatureOutOfShop:boolean;
  accountFields:Record<AccountField,Policy>;offerMailingListSignup:boolean;
  requirements:Requirement[];requiredAllVisits:boolean;
  redirectUrl:string|null;inShopRedirectUrl:string|null;resignAfterDays:number|null;
};
type Waiver=WaiverDraft&{id:string;revision:number};
type Catalog={
  waivers:Waiver[];serviceTypes:{code:string;name:string}[];
  locations:{code:string;name:string}[];
};
const fresh=():WaiverDraft=>({
  title:'',publicName:'',enabled:true,initialText:'',sections:[],sectionInstructions:'',
  afterText:'',readAndAgreeText:'',submitLabel:'Submit Waiver',
  requiresLoginInShop:false,requiresLoginOutOfShop:false,
  requireFullNameInShop:false,requireFullNameOutOfShop:false,
  requireSignatureInShop:true,requireSignatureOutOfShop:true,
  accountFields:Object.fromEntries(fields.map(f=>[f,'No'])) as
    Record<AccountField,Policy>,
  offerMailingListSignup:false,requirements:[],
  requiredAllVisits:false,redirectUrl:null,inShopRedirectUrl:null,resignAfterDays:null,
});
const accepted=(res:Response)=>res.ok;
// FloatHelm waiver text supports markdown from its section editor.
 // React nodes prevent raw HTML injection; links must use HTTPS.
const formatInline=(line:string):ReactNode[]=>{
  const tokens=line.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\(https:\/\/[^\s)]+\))/g)
    .filter(Boolean);
  return tokens.map((token,i)=>{
    if(token.startsWith('**')&&token.endsWith('**'))
      return <strong key={i}>{token.slice(2,-2)}</strong>;
    if(token.startsWith('*')&&token.endsWith('*'))
      return <em key={i}>{token.slice(1,-1)}</em>;
    const link=token.match(/^\[([^\]]+)\]\((https:\/\/[^\s)]+)\)$/);
    if(link)return <a key={i} href={link[2]} target="_blank" rel="noopener noreferrer">
      {link[1]}</a>;
    return token;
  });
};
const safeText=(v:string)=>v.split('\n').map((line,index)=>
  <span key={index} className="ecfs-waiver-text-line">{formatInline(line)}</span>);

/** Manager-only original Waivers settings, never a waiver acceptance form. */
export function WaiversWorkspace({locationCode,canEdit}:{
  locationCode:string;canEdit:boolean;
}){
  const [catalog,setCatalog]=useState<Catalog|null>(null);
  const [loadStatus,setLoadStatus]=useState('Loading Waivers…');
  const [search,setSearch]=useState('');
  const [statusFilter,setStatusFilter]=useState<'Enabled'|'Disabled'>('Enabled');
  const [editing,setEditing]=useState<Waiver|null| 'new'>(null);
  const [draft,setDraft]=useState<WaiverDraft>(fresh);
  const [preview,setPreview]=useState(false);
  const [resignAll,setResignAll]=useState(false);
  const [status,setStatus]=useState('');
  const [busy,setBusy]=useState(false);
  const reload=async(signal?:AbortSignal)=>{
    const response=await fetch('/api/v1/staff/waivers?'+
      new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store',signal});
    if(!accepted(response))throw Error('Waiver setup unavailable.');
    const body=await response.json() as {ok:boolean;data:Catalog};
    if(!body.ok||!Array.isArray(body.data?.waivers))throw Error('Invalid Waivers.');
    return body.data;
  };
  useEffect(()=>{
    const controller=new AbortController();
    setCatalog(null);setEditing(null);setLoadStatus('Loading Waivers…');
    void reload(controller.signal).then(data=>{
      if(controller.signal.aborted)return;
      setCatalog(data);setLoadStatus('');
    }).catch(()=>{if(!controller.signal.aborted)setLoadStatus('Waiver setup unavailable.');});
    return ()=>controller.abort();
  },[locationCode]);
  const matches=useMemo(()=>catalog?.waivers.filter(w=>w.enabled===(statusFilter==='Enabled')
    &&[w.title,w.publicName].some(text=>
      text.toLowerCase().includes(search.toLowerCase().trim())))??[],
  [catalog,search,statusFilter]);
  const start=(waiver:Waiver|'new')=>{
    setEditing(waiver);
    setDraft(waiver==='new'?fresh():{
      title:waiver.title,publicName:waiver.publicName,enabled:waiver.enabled,
      initialText:waiver.initialText,sections:[...waiver.sections],
      sectionInstructions:waiver.sectionInstructions??'',
      afterText:waiver.afterText,readAndAgreeText:waiver.readAndAgreeText,
      submitLabel:waiver.submitLabel,
      requiresLoginInShop:waiver.requiresLoginInShop,
      requiresLoginOutOfShop:waiver.requiresLoginOutOfShop,
      requireFullNameInShop:waiver.requireFullNameInShop,
      requireFullNameOutOfShop:waiver.requireFullNameOutOfShop,
      requireSignatureInShop:waiver.requireSignatureInShop,
      requireSignatureOutOfShop:waiver.requireSignatureOutOfShop,
      accountFields:{...fresh().accountFields,...waiver.accountFields},
      offerMailingListSignup:waiver.offerMailingListSignup,
      requirements:[...waiver.requirements],
      requiredAllVisits:waiver.requiredAllVisits,
      redirectUrl:waiver.redirectUrl,inShopRedirectUrl:waiver.inShopRedirectUrl,
      resignAfterDays:waiver.resignAfterDays});
    setPreview(false);setResignAll(false);setStatus('');
  };
  const modify=(key:keyof WaiverDraft,value:WaiverDraft[keyof WaiverDraft])=>
    setDraft(previous=>({...previous,[key]:value}));
  const updateSection=(index:number,text:string)=>
    modify('sections',draft.sections.map((section,i)=>i===index?text:section));
  const save=async(event:FormEvent)=>{
    event.preventDefault();if(!canEdit||busy||!editing)return;
    setBusy(true);setStatus('Saving Waiver…');
    try{
      const payload={location:locationCode,draft,
        ...(editing!=='new'?{expectedRevision:editing.revision}:{}),
        requiresResigning:resignAll};
      const response=await fetch('/api/v1/staff/waivers'+
        (editing==='new'?'':'/'+editing.id),{
        method:editing==='new'?'POST':'PATCH',credentials:'same-origin',
        headers:{'content-type':'application/json'},body:JSON.stringify(payload)});
      if(!response.ok){
        const body=await response.json().catch(()=>null) as
          {error?:{code?:string}}|null;
        throw Error(response.status===409?'This waiver was changed elsewhere. Reload before editing.':
          body?.error?.code??'Waiver could not be saved.');
      }
      const updated=await response.json() as {ok:boolean;data:Waiver};
      const next=await reload();setCatalog(next);
      setEditing(next.waivers.find(w=>w.id===updated.data.id)??updated.data);
      setStatus('Waiver saved. Revision '+updated.data.revision+'.');
      setResignAll(false);
    }catch(error){setStatus(error instanceof Error?error.message:'Unable to save Waiver.');}
    finally{setBusy(false);}
  };
  const flag=(key:keyof WaiverDraft,label:string)=>
    <label className="ecfs-waiver-flag">
      <input type="checkbox" checked={draft[key]===true}
        disabled={!canEdit||busy} onChange={e=>modify(key,e.target.checked)}/>{label}
    </label>;
  const requirementsSummary=(waiver:Waiver)=>
    waiver.requiredAllVisits?'All visits':
    waiver.requirements.length?waiver.requirements.map(rule=>{
      const type=catalog?.serviceTypes.find(s=>s.code===rule.serviceTypeCode)?.name??rule.serviceTypeCode;
      const loc=catalog?.locations.find(l=>l.code===rule.locationCode)?.name??rule.locationCode;
      return type+' appointments at '+loc;
    }).join(' · '):'No appointment requirements selected';
  return <section className="ecfs-waivers" aria-label="Waivers">
    <header className="ecfs-waivers-title">
      <h2>Waivers</h2>
      {editing&&<button type="button" onClick={()=>{setEditing(null);setPreview(false);setStatus('');}}>Waivers</button>}
    </header>
    {loadStatus&&<p role="status">{loadStatus}</p>}
    {catalog&&!editing&&<>
      <p className="ecfs-waivers-note">
        Optionally select a waiver to require before all visits, <strong>in addition</strong> to any Service-specific waiver.
      </p>
      {canEdit&&<div className="ecfs-waivers-add">
        <button type="button" onClick={()=>start('new')}>Add A Waiver</button>
      </div>}
      <div className="ecfs-waivers-filters" role="group" aria-label="Waiver status">
        {(['Enabled','Disabled'] as const).map(choice=>
          <button type="button" key={choice}
            aria-pressed={statusFilter===choice}
            onClick={()=>setStatusFilter(choice)}>{choice}</button>)}
        <input aria-label="Filter Waivers" placeholder="Search Waivers"
          type="search" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      <div className="ecfs-waivers-list" role="table" aria-label="Waiver definitions">
        <div role="row" className="ecfs-waivers-header">
          {['Waiver','Public Name','Required For','Revisions','Expiration'].map(title=>
            <strong role="columnheader" key={title}>{title}</strong>)}
        </div>
        {matches.map(w=><div role="row" key={w.id} className="ecfs-waivers-entry">
          <div role="cell">
            <button type="button" onClick={()=>start(w)}>{w.title}</button>
          </div>
          <span role="cell">{w.publicName}</span>
          <span role="cell">{requirementsSummary(w)}</span>
          <span role="cell">{w.revision}</span>
          <span role="cell">{w.resignAfterDays===null?'':w.resignAfterDays+' days'}
            <button type="button" className="ecfs-waivers-preview-link"
              onClick={()=>{start(w);setPreview(true);}}>preview</button>
          </span>
        </div>)}
      </div>
      {matches.length===0&&<p role="status">No {statusFilter.toLowerCase()} Waivers configured.</p>}
    </>}
    {catalog&&editing&&(preview?
      <div className="ecfs-waiver-preview">
        <div className="ecfs-waiver-preview-controls">
          <button type="button" onClick={()=>setPreview(false)}>Edit Waiver</button>
        </div>
        <h2>{draft.publicName||draft.title||'Waiver Preview'}</h2>
        {draft.initialText&&<p className="ecfs-waiver-preview-copy">{safeText(draft.initialText)}</p>}
        {draft.sectionInstructions&&<p className="ecfs-waiver-preview-copy">{draft.sectionInstructions}</p>}
        {draft.sections.map((section,i)=><label key={i} className="ecfs-waiver-preview-section">
          <input type="checkbox" disabled/>
          <span>{safeText(section)}</span>
        </label>)}
        {draft.afterText&&<p className="ecfs-waiver-preview-copy">{safeText(draft.afterText)}</p>}
        {draft.readAndAgreeText&&<p className="ecfs-waiver-preview-copy">{draft.readAndAgreeText}</p>}
        {(draft.requireSignatureInShop||draft.requireSignatureOutOfShop)&&<>
          <div className="ecfs-waiver-preview-signature" aria-label="Preview signature area"/>
          <span className="ecfs-waiver-preview-clear">clear signature</span>
        </>}
        <button type="button" disabled>{draft.submitLabel||'Submit Waiver'}</button>
        <strong className="ecfs-waiver-preview-warning">This is a preview and not usable.</strong>
      </div>:
      <form className="ecfs-waiver-edit" aria-label="Edit Waiver" onSubmit={e=>void save(e)}>
        <label className="ecfs-waiver-title-label">
          <span>Waiver</span>
          <input aria-label="Waiver" value={draft.title} required maxLength={140}
            disabled={!canEdit||busy} onChange={e=>modify('title',e.target.value)}/>
        </label>
        <fieldset><legend>Customer Facing Waiver Details</legend>
          <label>Public Name
            <input aria-label="Public Name" value={draft.publicName} required maxLength={140}
              disabled={!canEdit||busy} onChange={e=>modify('publicName',e.target.value)}/>
          </label>
          <label>Initial Text
            <textarea aria-label="Initial Text" rows={5} maxLength={40000}
              disabled={!canEdit||busy} value={draft.initialText}
              onChange={e=>modify('initialText',e.target.value)}/>
          </label>
          <strong>Checkmark Sections</strong>
          <input aria-label="Instructions above Checkmark Sections"
            value={draft.sectionInstructions} maxLength={5000} disabled={!canEdit||busy}
            onChange={e=>modify('sectionInstructions',e.target.value)}
            placeholder="Instructions above Checkmark Sections (optional)"/>
          {draft.sections.map((section,i)=><div key={i} className="ecfs-waiver-section-edit">
            <button type="button" aria-label={'Remove section '+(i+1)}
              disabled={!canEdit||busy}
              onClick={()=>modify('sections',draft.sections.filter((_,index)=>i!==index))}>X</button>
            <textarea aria-label={'Checkmark section '+(i+1)} value={section}
              disabled={!canEdit||busy} rows={3} maxLength={14000}
              onChange={e=>updateSection(i,e.target.value)}/>
            <button type="button" aria-label={'Move section '+(i+1)+' up'}
              disabled={!canEdit||busy||i===0}
              onClick={()=>{const next=[...draft.sections];[next[i-1],next[i]]=[next[i],next[i-1]];
                modify('sections',next);}}>↑</button>
            <button type="button" aria-label={'Move section '+(i+1)+' down'}
              disabled={!canEdit||busy||i===draft.sections.length-1}
              onClick={()=>{const next=[...draft.sections];[next[i],next[i+1]]=[next[i+1],next[i]];
                modify('sections',next);}}>↓</button>
          </div>)}
          <button type="button" className="ecfs-waiver-add-section" disabled={!canEdit||busy}
            onClick={()=>modify('sections',[...draft.sections,''])}>+ add section</button>
          <label>Additional Text After Checkmark Sections
            <textarea aria-label="Additional Text After Checkmark Sections" rows={3}
              maxLength={20000} value={draft.afterText} disabled={!canEdit||busy}
              onChange={e=>modify('afterText',e.target.value)}/>
          </label>
          <label>Read-and-Agree Text (optional)
            <input aria-label="Read-and-Agree Text (optional)"
              value={draft.readAndAgreeText} maxLength={5000} disabled={!canEdit||busy}
              onChange={e=>modify('readAndAgreeText',e.target.value)}/>
          </label>
          <label>Submit button
            <input aria-label="Submit button" value={draft.submitLabel} maxLength={120}
              required disabled={!canEdit||busy}
              onChange={e=>modify('submitLabel',e.target.value)}/>
          </label>
          <label className="ecfs-waiver-resign-all">
            <input type="checkbox" checked={resignAll} disabled={!canEdit||busy}
              onChange={e=>setResignAll(e.target.checked)}/>
            This Change Requires Re-Signing by All Customers
          </label>
        </fieldset>
        <fieldset className="ecfs-waiver-usage">
          <legend>Require Login:</legend>
          <div>{flag('requiresLoginInShop','In Shop')}
            {flag('requiresLoginOutOfShop','Out of Shop')}</div>
          <strong>Require Writing Full Name:</strong>
          <div>{flag('requireFullNameInShop','In Shop')}
            {flag('requireFullNameOutOfShop','Out of Shop')}</div>
          <strong>Require Signature:</strong>
          <div>{flag('requireSignatureInShop','In Shop')}
            {flag('requireSignatureOutOfShop','Out of Shop')}</div>
        </fieldset>
        <fieldset className="ecfs-waiver-account-fields">
          <legend>Collect Account Fields</legend>
          <table>
            <thead><tr><th></th><th>No</th><th>Ask</th><th>Require</th></tr></thead>
            <tbody>{fields.map(field=><tr key={field}>
              <td>{field}</td>
              {(['No','Ask','Require'] as const).map(option=><td key={option}>
                <input type="radio" name={'waiver-field-'+field}
                  aria-label={field+' '+option}
                  checked={draft.accountFields[field]===option}
                  disabled={!canEdit||busy}
                  onChange={()=>modify('accountFields',{
                    ...draft.accountFields,[field]:option})}/>
              </td>)}
            </tr>)}</tbody>
          </table>
          {flag('offerMailingListSignup','Offer Mailing List Signup')}
        </fieldset>
        <fieldset className="ecfs-waiver-requirements">
          <legend>Requirements</legend>
          {flag('requiredAllVisits','Required before all visits')}
          {draft.requirements.map((r,i)=><div key={i} className="ecfs-waiver-requirement">
            <span>Required for</span>
            <select aria-label={'Requirement service type '+(i+1)}
              value={r.serviceTypeCode} disabled={!canEdit||busy}
              onChange={e=>modify('requirements',draft.requirements.map((v,index)=>
                index===i?{...v,serviceTypeCode:e.target.value}:v))}>
              {catalog.serviceTypes.map(t=><option value={t.code} key={t.code}>
                All {t.name} Appointments</option>)}
            </select>
            <span>at</span>
            <select aria-label={'Requirement location '+(i+1)}
              value={r.locationCode} disabled={!canEdit||busy}
              onChange={e=>modify('requirements',draft.requirements.map((v,index)=>
                index===i?{...v,locationCode:e.target.value}:v))}>
              {catalog.locations.map(loc=><option value={loc.code} key={loc.code}>
                {loc.name}</option>)}
            </select>
            <button type="button" disabled={!canEdit||busy}
              onClick={()=>modify('requirements',draft.requirements.filter((_,index)=>i!==index))}>
              remove</button>
          </div>)}
          <button type="button" className="ecfs-waiver-add-section"
            disabled={!canEdit||busy||!catalog.serviceTypes.length||!catalog.locations.length}
            onClick={()=>modify('requirements',[...draft.requirements,{
              serviceTypeCode:catalog.serviceTypes[0].code,
              locationCode:locationCode,
            }])}>+ add requirement</button>
        </fieldset>
        <fieldset className="ecfs-waiver-redirects">
          <label>Redirect customers to URL after completing out of shop (optional)
            <input type="url" placeholder="https://..." aria-label="Out of Shop Redirect URL"
              value={draft.redirectUrl??''} disabled={!canEdit||busy}
              onChange={e=>modify('redirectUrl',e.target.value||null)}/>
          </label>
          <label>Redirect customers to URL after completing in-shop (optional)
            <input type="url" placeholder="https://..." aria-label="In Shop Redirect URL"
              value={draft.inShopRedirectUrl??''} disabled={!canEdit||busy}
              onChange={e=>modify('inShopRedirectUrl',e.target.value||null)}/>
          </label>
          <label>Customers have to re-sign the waiver after
            <input type="number" min={1} max={3650} aria-label="Re-sign after days"
              placeholder="days" value={draft.resignAfterDays??''}
              disabled={!canEdit||busy}
              onChange={e=>modify('resignAfterDays',e.target.value===''?null:Number(e.target.value))}/>
            days. (optional)
          </label>
          {flag('enabled','Enabled')}
        </fieldset>
        <div className="ecfs-waiver-actions">
          {canEdit&&<button type="submit" className="ecfs-waiver-save"
            disabled={busy}>{busy?'Saving…':'Save Waiver'}</button>}
          <button type="button" onClick={()=>setEditing(null)}>cancel</button>
          <button type="button" onClick={()=>setPreview(true)}>preview</button>
        </div>
        {status&&<p role="status">{status}</p>}
      </form>)}
  </section>;
}
